'use client'

import { useCallback, useState } from 'react'
import TutorialCard from './TutorialCard'
import TutorialPlayerModal from './TutorialPlayerModal'
import { HelpTutorial, useTutorials } from './tutorials'

const TutorialsPage = () => {
  const { groups: tutorialGroups, tutorials, status } = useTutorials()
  const [selectedTutorial, setSelectedTutorial] = useState<HelpTutorial | null>(null)
  const closeTutorial = useCallback(() => setSelectedTutorial(null), [])

  const isLoading = status === 'idle' || status === 'loading'
  const isError = status === 'error'
  const isEmpty = status === 'loaded' && tutorials.length === 0

  return (
    <div className="pr-xxl pb-xxl flex flex-col gap-xxxl">
      <TutorialPlayerModal tutorial={selectedTutorial} onClose={closeTutorial} />

      <div className="flex flex-col gap-xs border-b border-gray-200 pb-md">
        <div className="text-title-lg text-gray-900">Admin portal tutorials</div>
        <div className="max-w-[760px] text-text-md text-gray-400">
          Select a tutorial to open its video. Videos only play after you press Play.
        </div>
      </div>

      {isLoading && (
        <div className="flex h-[150px] items-center justify-center rounded-md bg-gray-50 text-text-md text-gray-300">
          Loading tutorials…
        </div>
      )}

      {isError && (
        <div className="flex h-[150px] items-center justify-center rounded-md bg-gray-50 text-text-md text-gray-400">
          Tutorials are unavailable right now. Please try again later.
        </div>
      )}

      {isEmpty && (
        <div className="flex h-[150px] items-center justify-center rounded-md bg-gray-50 text-text-md text-gray-400">
          No tutorials have been published yet.
        </div>
      )}

      {tutorialGroups.map((group) => {
        const groupTutorials = tutorials.filter((tutorial) => tutorial.groupId === group.id)

        if (groupTutorials.length === 0) return null

        return (
          <section key={group.id} className="flex flex-col gap-md">
            <div className="flex flex-col gap-xs">
              <h2 className="text-title-lg text-gray-900">{group.title}</h2>
              <p className="text-text-md text-gray-400">{group.description}</p>
            </div>

            <div className="grid grid-cols-1 gap-lg xl:grid-cols-2">
              {groupTutorials.map((tutorial) => (
                <TutorialCard key={tutorial.id} tutorial={tutorial} onPlay={setSelectedTutorial} />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

export default TutorialsPage
